import React, { PureComponent } from "react";
class Portrait extends PureComponent {
  getStockItems = (widget) => {
    if (Array.isArray(widget?.stockData) && widget.stockData.length) {
      return widget.stockData;
    }

    if (!widget?.stockData) return [];

    const fallbackSymbol =
      widget?.data?.symbol ||
      widget?.stockData?.["Meta Data"]?.["2. Symbol"] ||
      widget?.stockData?.["Meta Data"]?.["1. Symbol"];

    if (!fallbackSymbol) return [];

    return [
      {
        symbol: fallbackSymbol,
        companyName: widget?.data?.companyName || fallbackSymbol,
        data: widget.stockData,
      },
    ];
  };
  getLatestStockData = (stockData) => {
    if (!stockData) return null;

    const timeSeriesKeys = Object.keys(stockData).filter(
      (key) => key.includes("Time Series") || key.includes("Meta Data")
    );

    if (timeSeriesKeys.length === 0) return null;

    const timeSeriesKey = timeSeriesKeys.find((key) =>
      key.includes("Time Series")
    );
    if (!timeSeriesKey) return null;

    const timeSeries = stockData[timeSeriesKey];
    if (!timeSeries) return null;

    const timestamps = Object.keys(timeSeries).sort().reverse();
    if (timestamps.length === 0) return null;

    const latestTimestamp = timestamps[0];
    const latestData = timeSeries[latestTimestamp];
    const previousTimestamp = timestamps[1] || null;
    const previousData = previousTimestamp
      ? timeSeries[previousTimestamp]
      : null;

    const metaData = stockData["Meta Data"] || {};
    const symbol = metaData["2. Symbol"] || metaData["1. Symbol"] || "";

    const currentClose = parseFloat(
      latestData["4. close"] ||
        latestData["4. Close"] ||
        latestData["5. adjusted close"] ||
        latestData["5. Adjusted Close"] ||
        0
    );
    const previousClose = previousData
      ? parseFloat(
          previousData["4. close"] ||
            previousData["4. Close"] ||
            previousData["5. adjusted close"] ||
            previousData["5. Adjusted Close"] ||
            currentClose
        )
      : parseFloat(
          latestData["1. open"] || latestData["1. Open"] || currentClose
        );

    return {
      symbol,
      timestamp: latestTimestamp,
      open: parseFloat(latestData["1. open"] || latestData["1. Open"] || 0),
      high: parseFloat(latestData["2. high"] || latestData["2. High"] || 0),
      low: parseFloat(latestData["3. low"] || latestData["3. Low"] || 0),
      close: currentClose,
      previousClose: previousClose,
      volume: parseFloat(
        latestData["5. volume"] ||
          latestData["5. Volume"] ||
          latestData["6. volume"] ||
          latestData["6. Volume"] ||
          0
      ),
      allData: timeSeries,
      timestamps,
    };
  };

  getDayStats = (stockData) => {
    const data = this.getLatestStockData(stockData);
    if (!data || !data.allData) return { dayLow: 0, dayHigh: 0 };

    // Check if this is intraday data (has time in timestamp) or daily/weekly/monthly (date only)
    const isIntraday = data.timestamps[0] && data.timestamps[0].includes(" ");

    if (isIntraday) {
      // For intraday data, filter by today's date
      const today = new Date().toISOString().split("T")[0];
      const todayData = data.timestamps
        .filter((ts) => ts.startsWith(today))
        .map((ts) => data.allData[ts])
        .filter(Boolean);

      if (todayData.length > 0) {
        const highs = todayData.map((d) =>
          parseFloat(d["2. high"] || d["2. High"] || 0)
        );
        const lows = todayData.map((d) =>
          parseFloat(d["3. low"] || d["3. Low"] || 0)
        );
        return {
          dayLow: Math.min(...lows),
          dayHigh: Math.max(...highs),
        };
      }
    }

    // For daily/weekly/monthly data, use the latest entry's high/low directly
    // The latest entry represents the current trading period
    const latestData = data.allData[data.timestamps[0]];
    if (latestData) {
      return {
        dayLow: parseFloat(latestData["3. low"] || latestData["3. Low"] || 0),
        dayHigh: parseFloat(
          latestData["2. high"] || latestData["2. High"] || 0
        ),
      };
    }

    return { dayLow: 0, dayHigh: 0 };
  };

  renderEmptyState = (width, height, message, subtext) => (
    <div
      className="d-flex justify-content-center align-items-center"
      style={{
        width: width + "px",
        height: height + "px",
        backgroundColor: "#1a1a1a",
        color: "#fff",
      }}
    >
      <div className="text-center">
        <div style={{ fontSize: "24px", marginBottom: "10px" }}>{message}</div>
        {subtext && (
          <div style={{ fontSize: "14px", color: "rgba(255, 255, 255, 0.7)" }}>
            {subtext}
          </div>
        )}
      </div>
    </div>
  );

  renderMultiStocks = (stockItems, width, height) => {
    const shorterSide = Math.min(width, height);
    const cardPadding = Math.max(shorterSide * 0.03, 12);
    const priceSize = Math.max(shorterSide * 0.08, 24);
    const titleSize = Math.max(shorterSide * 0.04, 14);
    const changeSize = Math.max(shorterSide * 0.035, 12);
    const count = stockItems.length;
    const rows = count === 2 ? "1fr" : "1fr 1fr";

    return (
      <div
        style={{
          width: width + "px",
          height: height + "px",
          backgroundColor: "#1a1a1a",
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gridTemplateRows: rows,
          color: "#fff",
        }}
      >
        {stockItems.map((item, idx) => {
          const cellStyle = {
            padding: cardPadding + "px",
            border: "1px solid rgba(255,255,255,0.08)",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          };

          if (count === 3 && idx === 2) {
            cellStyle.gridColumn = "1 / span 2";
          }

          const latest = this.getLatestStockData(item.data);
          if (!latest) {
            return (
              <div key={item.symbol} style={cellStyle}>
                <div style={{ fontSize: titleSize + "px", fontWeight: 700 }}>
                  {item.companyName || item.symbol}
                </div>
                <div
                  style={{
                    fontSize: changeSize + "px",
                    color: "rgba(255,255,255,0.6)",
                  }}
                >
                  Data unavailable
                </div>
              </div>
            );
          }
          const dayStats = this.getDayStats(item.data);
          const previousClose = latest.previousClose || latest.open;
          const currentPrice = latest.close;
          const priceChange = currentPrice - previousClose;
          const priceChangePercent =
            previousClose !== 0 ? (priceChange / previousClose) * 100 : 0;
          const isPositive = priceChange >= 0;

          return (
            <div key={item.symbol} style={cellStyle}>
              <div style={{ fontSize: titleSize + "px", fontWeight: 700 }}>
                {item.companyName || item.symbol}
              </div>
              <div
                style={{
                  fontSize: priceSize + "px",
                  fontWeight: 700,
                  lineHeight: 1.1,
                }}
              >
                {currentPrice.toFixed(2)}
              </div>
              <div
                style={{
                  fontSize: changeSize + "px",
                  fontWeight: 700,
                  color: isPositive ? "#10b981" : "#ef4444",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <i
                  className={`bi ${
                    isPositive ? "bi-arrow-up" : "bi-arrow-down"
                  }`}
                />
                <span>
                  {isPositive ? "+" : ""}
                  {priceChange.toFixed(2)} ({isPositive ? "+" : ""}
                  {priceChangePercent.toFixed(2)}%)
                </span>
              </div>
              <div
                className="d-flex justify-content-between mt-2"
                style={{ fontSize: Math.max(shorterSide * 0.03, 10) + "px" }}
              >
                <span>Low: {dayStats.dayLow.toFixed(2)}</span>
                <span>High: {dayStats.dayHigh.toFixed(2)}</span>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  renderSingleStock = (item, width, height) => {
    const stockData = this.getLatestStockData(item.data);
    if (!stockData) {
      return this.renderEmptyState(width, height, "Invalid stock data");
    }
    const dayStats = this.getDayStats(item.data);

    const previousClose = stockData.previousClose || stockData.open;
    const currentPrice = stockData.close;
    const priceChange = currentPrice - previousClose;
    const priceChangePercent =
      previousClose !== 0 ? (priceChange / previousClose) * 100 : 0;
    const isPositive = priceChange >= 0;

    const shorterSide = Math.min(width, height);

    return (
      <div
        style={{
          width: width + "px",
          height: height + "px",
          backgroundColor: "#1a1a1a",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          color: "#fff",
          padding: width * 0.05 + "px",
          boxSizing: "border-box",
          overflow: "hidden",
        }}
      >
        <div className="text-center mb-4">
          <h2
            style={{
              fontSize: Math.min(shorterSide * 0.06, 36) + "px",
              fontWeight: 700,
              color: "#fff",
              marginBottom: "20px",
            }}
          >
            {item.companyName || stockData.symbol}
          </h2>
        </div>

        <div className="text-center mb-4">
          <div
            style={{
              fontSize: Math.min(shorterSide * 0.22, 112) + "px",
              fontWeight: 700,
              color: "#fff",
              marginBottom: "16px",
            }}
          >
            {currentPrice.toFixed(2)}
          </div>

          <div
            style={{
              fontSize: Math.min(shorterSide * 0.055, 28) + "px",
              fontWeight: 700,
              color: isPositive ? "#10b981" : "#ef4444",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
            }}
          >
            <i
              className={`bi ${isPositive ? "bi-arrow-up" : "bi-arrow-down"}`}
              style={{ fontSize: "28px" }}
            />
            <span>
              {isPositive ? "+" : ""}
              {priceChange.toFixed(2)} ({isPositive ? "+" : ""}
              {priceChangePercent.toFixed(3)}%)
            </span>
          </div>
        </div>

        <div
          className="d-flex justify-content-between mt-4"
          style={{
            width: "80%",
            maxWidth: "600px",
            fontSize: Math.min(shorterSide * 0.045, 24) + "px",
            paddingTop: "30px",
            borderTop: "1px solid rgba(255, 255, 255, 0.2)",
          }}
        >
          <div className="text-center">
            <div
              style={{
                color: "rgba(255, 255, 255, 0.7)",
                fontWeight: 700,
                marginBottom: "8px",
              }}
            >
              Day Low
            </div>
            <div
              style={{
                color: "#fff",
                fontWeight: 700,
                fontSize: Math.min(shorterSide * 0.055, 28) + "px",
              }}
            >
              {dayStats.dayLow.toFixed(2)}
            </div>
          </div>
          <div className="text-center">
            <div
              style={{
                color: "rgba(255, 255, 255, 0.7)",
                fontWeight: 700,
                marginBottom: "8px",
              }}
            >
              Day High
            </div>
            <div
              style={{
                color: "#fff",
                fontWeight: 700,
                fontSize: Math.min(shorterSide * 0.055, 28) + "px",
              }}
            >
              {dayStats.dayHigh.toFixed(2)}
            </div>
          </div>
        </div>
      </div>
    );
  };

  render() {
    const { widget } = this.props;
    const height = window.innerHeight;
    const width = window.innerWidth;
    const stockItems = this.getStockItems(widget);

    if (!stockItems.length) {
      return this.renderEmptyState(
        width,
        height,
        "No stock data available",
        "Please configure the stock widget"
      );
    }

    if (stockItems.length === 1) {
      return this.renderSingleStock(stockItems[0], width, height);
    }

    return this.renderMultiStocks(stockItems, width, height);
  }
}

export default Portrait;
